import { hubPath, hubSlugFor, type HubIndex, type HubKind, type MoreByArtist } from "@shared/hubs";
import { getPreloadedData } from "@/lib/hydration";

let indexPromise: Promise<HubIndex> | null = null;

const EMPTY_INDEX: HubIndex = { artists: {}, styles: {}, museums: {} };

// Artist, style and museum name -> hub slug, for names that have a hub page.
export function loadHubIndex(): Promise<HubIndex> {
  if (!indexPromise) {
    indexPromise = fetch("/hubs.json")
      .then((r) => (r.ok ? r.json() : EMPTY_INDEX))
      .then((i) => ({ ...EMPTY_INDEX, ...i }))
      .catch(() => EMPTY_INDEX);
  }
  return indexPromise;
}

export function hubHref(index: HubIndex | null, kind: HubKind, name?: string): string | null {
  if (!name) return null;
  const slug = hubSlugFor(index, kind, name);
  return slug ? hubPath(kind, slug) : null;
}

// Hub pages carry their data in the static HTML. On a full page load it is
// read from the page; after in-app navigation the page is fetched for it.
export async function loadPageData<T>(key: string, pathname: string): Promise<T | null> {
  const preloaded = getPreloadedData();
  if (preloaded && preloaded[key]) return preloaded[key] as T;
  try {
    const resp = await fetch(pathname.endsWith("/") ? pathname : `${pathname}/`);
    if (!resp.ok) return null;
    const html = await resp.text();
    const m = html.match(/<script id="preloaded-data" type="application\/json">([\s\S]*?)<\/script>/);
    if (!m) return null;
    const data = JSON.parse(m[1]);
    return (data?.[key] as T) ?? null;
  } catch {
    return null;
  }
}

// The "More by {artist}" links of an artwork page. On a full page load they are
// read from the page's data (left in place for the knowledge graph); after
// in-app navigation the artwork's static page is fetched for them.
export async function loadMoreByArtist(id: number): Promise<MoreByArtist | null> {
  const parse = (text?: string | null) => {
    if (!text) return null;
    try {
      const data = JSON.parse(text);
      return Number(data?.currentArtworkId) === id ? ((data.moreByArtist as MoreByArtist) ?? null) : undefined;
    } catch {
      return undefined;
    }
  };
  const inPage = parse(document.getElementById("preloaded-data")?.textContent);
  if (inPage !== undefined) return inPage;
  try {
    const resp = await fetch(`/artwork/${id}/`);
    if (!resp.ok) return null;
    const m = (await resp.text()).match(/<script id="preloaded-data" type="application\/json">([\s\S]*?)<\/script>/);
    return parse(m?.[1]) ?? null;
  } catch {
    return null;
  }
}
